import {
	CHANGE_DEVICE,
	INIT,
	SITE_INIT,
	OPEN_MENU,
	OPEN_CART,
	COOKIE_CONSENT,
	SET_QUERY,
	REMOVE_NOTIFICATION,
	CHANGE_LANGUAGE,
	CHANGE_UNITS,
	UPDATE_EVENT_DETAILS,
	UPDATE_EVENT_DETAILS_ERRORS,
	PARTICIPANTS_REGISTERED,
	PARTICIPANTS_ERROR,
	ACUITY_IFRAME_HEIGHT,
} from '../actions/environment';
import { unique } from '../utils/array';
import { setCookie, addClass, removeClass } from '../utils/dom';
import { urlify } from '../utils/string';

const initialState = {
	properties: {
		openCart: 0,
		siteInitiated: false,
		siteUpdated: false,
	},
	setup: {},
	header: {},
	footer: {},
	searchInputText: '',
	hashLocation: '',
	navigation: {
		open: false,
	},
	acuityIframeHeight: 1100,
	pagination: {},
	favourites: [],
	eventDetails: {
		allowFriends: false,
		subscribeToNewsletter: false,
		acceptTerms: false,
		serverErrors: [],
		errors: {},
	},
};

const environment = (state = initialState, action) => {
	switch (action.type) {
		case INIT:
			return {
				...state,
				properties: {
					...state.properties,
					siteClosed: action.initData.siteClosed || false,
					siteInitiated: true,
				},
				setup: {
					...state.setup,
					...action.initData.setup,
				},
				header: {
					...state.header,
					...action.initData.header,
				},
				magazineSnippets: action.initData.magazineSnippets,
				footer: {
					...state.footer,
					...action.initData.footer,
				},
				navigation: {
					...state.navigation,
					...action.initData.navigation,
				},
			};

		case CHANGE_DEVICE:
			return {
				...state,
				setup: {
					...state.setup,
					breakPoint: action.breakPoint,
				},
			};

		case ACUITY_IFRAME_HEIGHT:
			return {
				...state,
				acuityIframeHeight: action.height,
			};

		case 'SITE_UPDATED':
			return {
				...state,
				properties: {
					...state.properties,
					siteUpdated: true,
				},
			};

		case 'UPDATE_PAGINATION':
			return {
				...state,
				pagination: {
					...state.pagination,
					[action.currentPath]: action.currentPage,
				},
			};

		case 'UPDATE_HASH':
			return {
				...state,
				hashLocation: action.hash,
			};

		case 'PAGE_PATHS':
			return {
				...state,
				pageType: action.pageType,
				pagePaths: action.paths,
			};

		case SITE_INIT:
			return {
				...state,
				properties: {
					...state.properties,
					siteInitiated: true,
				},
			};

		case 'UPDATE_COUNTRY': {
			if (typeof document !== 'undefined') {
				// Is set on server unconditionally so only valid for client
				setCookie('settings', [state.setup.language, action.country, parseInt(action.market, 10), parseInt(action.pricelist, 10)]);
			}
			const shippable = state.setup.countries[action.country].shipTo;
			return {
				...state,
				setup: {
					...state.setup,
					country: action.country,
					currency: action.currency,
					market: action.market,
					units: action.country === 'US' ? 'inches' : state.setup.units,
					pricelist: action.pricelist,
					unableToSendToCountry: !shippable,
				},
			};
		}

		case CHANGE_LANGUAGE: {
			if (typeof document !== 'undefined') {
				// Is set on server unconditionally so only valid for client
				setCookie('settings', [action.language, state.setup.country, parseInt(state.setup.market, 10), parseInt(state.setup.pricelist, 10)]);
			}
			return {
				...state,
				setup: {
					...state.setup,
					language: action.language,
				},
			};
		}

		case CHANGE_UNITS: {
			return {
				...state,
				setup: {
					...state.setup,
					units: action.units,
				},
			};
		}

		case OPEN_MENU: {
			const open = action.open !== undefined ? action.open : !state.navigation.open;
			if (typeof document !== 'undefined') {
				// Is set on server unconditionally so only valid for client
				if (open) addClass(document.body, 'u_menuIsOpen');
				else removeClass(document.body, 'u_menuIsOpen');
			}
			return {
				...state,
				navigation: {
					...state.navigation,
					open,
				},
			};
		}

		case OPEN_CART:
			return {
				...state,
				properties: {
					...state.properties,
					openCart: (state.properties.openCart + 1) % 3,
				},
			};

		case COOKIE_CONSENT: {
			return {
				...state,
				setup: {
					...state.setup,
					cookieConsentSet: 1,
				},
			};
		}

		case SET_QUERY:
			return {
				...state,
				setup: {
					...state.setup,
					[action.key]: action.value,
				},
			};

		case REMOVE_NOTIFICATION:
			return {
				...state,
				header: {
					...state.header,
					[action.notificationType]: (
						state.header[action.notificationType].map((notification) => {
							if (urlify(notification.name) === action.notificationName) {
								return {
									...notification,
									removed: true,
								};
							}
							return notification;
						})
					),
				},
			};

		case 'SEARCH_RESULTS':
			return {
				...state,
				searchInputText: (action.text || '').replace(/-/g, ' '),
			};

		case 'TOGGLE_FAVOURITE': {
			let favourites = [...state.favourites];
			const index = favourites.indexOf(action.productId);

			if (index === -1 || action.force) {
				favourites = [...favourites, action.productId];
			} else {
				favourites = [...favourites.slice(0, index), ...favourites.slice(index + 1)];
			}

			favourites = unique(favourites);
			if (typeof window !== 'undefined') localStorage.setItem('favourites', JSON.stringify(favourites));

			return {
				...state,
				favourites,
			};
		}

		case 'UPDATE_FAVOURITES':
			return {
				...state,
				favourites: [...action.favourites],
			};

		case UPDATE_EVENT_DETAILS:
			return {
				...state,
				eventDetails: {
					...state.eventDetails,
					[action.id]: action.value ? action.value : !state.eventDetails[action.id],
					eventRegistrationFailed: false,
					eventRegistrationSucceded: false,
					serverErrors: [],
				},
			};

		case UPDATE_EVENT_DETAILS_ERRORS:
			return {
				...state,
				eventDetails: {
					...state.eventDetails,
					errors: {
						...state.eventDetails.errors,
						[action.id]: action.error,
					},
				},
			};

		case PARTICIPANTS_REGISTERED:
			return {
				...state,
				eventDetails: {
					...initialState.eventDetails,
					eventRegistrationSucceded: true,
					eventRegistrationFailed: false,
					serverErrors: [],
				},
			};

		case PARTICIPANTS_ERROR:
			return {
				...state,
				eventDetails: {
					...state.eventDetails,
					eventRegistrationFailed: true,
					eventRegistrationSucceded: false,
					serverErrors: action.errors ? [...action.errors] : [],
				},
			};

		default:
			return state;
	}
};

export { initialState as environmentState };
export default environment;
